import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { Socket } from 'socket.io-client';
import { createSocket } from '../services/socket';
import type {
  CardSetType,
  ClientToServerEvents,
  Player,
  PublicGameState,
  ServerToClientEvents,
} from '../types/game';

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

interface GameContextType {
  gameId: string | null;
  players: Player[];
  currentPlayer: Player | null;
  currentPlayerId: string | null;
  cardOptions: string[];
  cardSet: CardSetType;
  votesRevealed: boolean;
  endVotingRequestCount: number;
  hasRequestedEndVoting: boolean;
  connectionStatus: ConnectionStatus;
  setGameId: (id: string) => void;
  joinGame: (name: string) => void;
  castVote: (vote: string) => void;
  resetGame: () => void;
  changeCardSet: (cardSet: CardSetType) => void;
  toggleEndVoting: () => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

const shouldDebug = (): boolean => {
  if (import.meta.env.DEV) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem('DEBUG_GAME') === 'true';
  } catch (error) {
    console.warn('[GameContext] Unable to read DEBUG_GAME flag from localStorage.', error);
    return false;
  }
};

const debug = (...args: unknown[]) => {
  if (shouldDebug()) {
    console.info('[GameContext]', ...args);
  }
};

const CARD_SET_STORAGE_KEY = 'panda-poker-card-set';
const PLAYER_IDENTITY_STORAGE_PREFIX = 'panda-poker-player:';

interface StoredPlayerIdentity { name: string; playerId: string; reconnectToken?: string; }

const getStoredPlayerIdentity = (gameId: string): StoredPlayerIdentity | null => {
  try {
    const stored = window.localStorage.getItem(`${PLAYER_IDENTITY_STORAGE_PREFIX}${gameId}`);
    if (!stored) return null;
    const identity = JSON.parse(stored) as Partial<StoredPlayerIdentity>;
    return typeof identity.name === 'string' && typeof identity.playerId === 'string'
      && (identity.reconnectToken === undefined || typeof identity.reconnectToken === 'string')
      ? { name: identity.name, playerId: identity.playerId, reconnectToken: identity.reconnectToken } : null;
  } catch { return null; }
};

const storePlayerIdentity = (gameId: string, identity: StoredPlayerIdentity): void => {
  try { window.localStorage.setItem(`${PLAYER_IDENTITY_STORAGE_PREFIX}${gameId}`, JSON.stringify(identity)); }
  catch (error) { console.warn('[GameContext] Unable to save player reconnect identity.', error); }
};

const getStoredCardSet = (): CardSetType | null => {
  try {
    const stored = window.localStorage.getItem(CARD_SET_STORAGE_KEY);
    if (stored === 'fibonacci' || stored === 'tshirt') return stored;
    return null;
  } catch {
    return null;
  }
};

const storeCardSet = (cardSet: CardSetType): void => {
  try {
    window.localStorage.setItem(CARD_SET_STORAGE_KEY, cardSet);
  } catch (error) {
    console.warn('[GameContext] Unable to save card set to localStorage.', error);
  }
};

interface GameProviderProps {
  children: ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [gameId, setGameIdState] = useState<string | null>(null);
  const [socket, setSocket] = useState<GameSocket | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [currentPlayerId, setCurrentPlayerId] = useState<string | null>(null);
  const [cardOptions, setCardOptions] = useState<string[]>(['XS', 'S', 'M', 'L', 'XL', '?', '☕']);
  const [cardSet, setCardSet] = useState<CardSetType>('tshirt');
  const [votesRevealed, setVotesRevealed] = useState(false);
  const [endVotingRequesterIds, setEndVotingRequesterIds] = useState<string[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [pendingJoin, setPendingJoin] = useState<string | null>(null);
  const [localSelectedVote, setLocalSelectedVote] = useState<string | null>(null);
  const votesRevealedRef = useRef(false);
  const joinNameRef = useRef<string | null>(null);
  const authoritativePlayerIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!gameId) {
      return;
    }

    const newSocket = createSocket();
    setConnectionStatus('connecting');

    // Set a timeout to detect if connection is taking too long
    const connectionTimeout = setTimeout(() => {
      if (newSocket.disconnected) {
        console.warn('[GameContext] Socket connection timeout after 10 seconds');
        setConnectionStatus('disconnected');
      }
    }, 10000);

    const handleConnect = () => {
      clearTimeout(connectionTimeout);
      setConnectionStatus('connected');
      const identity = getStoredPlayerIdentity(gameId);
      if (identity) newSocket.emit('joinGame', { gameId, name: identity.name, reconnectToken: identity.reconnectToken });
    };

    const handleDisconnect = () => {
      setConnectionStatus('disconnected');
      setPlayers([]);
      setCurrentPlayerId(null);
      setVotesRevealed(false);
      setEndVotingRequesterIds([]);
    };

    const handleJoined = ({ playerId, cardOptions: options, reconnectToken }: { playerId: string; cardOptions: string[]; reconnectToken?: string }) => {
      if (authoritativePlayerIdRef.current && authoritativePlayerIdRef.current !== playerId) setLocalSelectedVote(null);
      authoritativePlayerIdRef.current = playerId;
      setCurrentPlayerId(playerId);
      const existingIdentity = getStoredPlayerIdentity(gameId);
      const name = joinNameRef.current ?? existingIdentity?.name;
      if (name) storePlayerIdentity(gameId, { name, playerId, ...(reconnectToken ? { reconnectToken } : {}) });
      joinNameRef.current = null;
      if (options?.length) {
        setCardOptions(options);
      }
      setPendingJoin(null);
    };

    const handleGameState = (state: PublicGameState) => {
      debug('received gameState', state);

      // Hide other players' votes until revealed (current player's vote tracked locally)
      const sanitizedPlayers = state.votesRevealed
        ? state.players
        : state.players.map((player) => ({ ...player, vote: undefined }));

      setPlayers(sanitizedPlayers);
      const current = authoritativePlayerIdRef.current ? state.players.find((player) => player.id === authoritativePlayerIdRef.current) : undefined;
      if (current && !current.hasVoted) setLocalSelectedVote(null);

      // Clear local vote when a new round starts (votesRevealed transitions from true to false)
      if (votesRevealedRef.current && !state.votesRevealed) {
        setLocalSelectedVote(null);
      }
      votesRevealedRef.current = state.votesRevealed;

      setVotesRevealed(state.votesRevealed);
      setEndVotingRequesterIds(state.endVotingRequesterIds ?? []);

      debug('sanitized players', sanitizedPlayers, 'votesRevealed', state.votesRevealed);
      if (state.cardOptions?.length) {
        setCardOptions(state.cardOptions);
      }
      if (state.cardSet) {
        setCardSet(state.cardSet);
        storeCardSet(state.cardSet);
      }
    };

    const handleError = ({ message }: { message: string }) => {
      console.warn('[socket] error', message);
      setConnectionStatus('disconnected');
    };

    const handleConnectError = (error: Error) => {
      clearTimeout(connectionTimeout);
      console.error('[socket] Connection error:', error);
      setConnectionStatus('disconnected');
    };

    newSocket.on('connect', handleConnect);
    newSocket.on('disconnect', handleDisconnect);
    newSocket.on('joined', handleJoined);
    newSocket.on('gameState', handleGameState);
    newSocket.on('errorMessage', handleError);
    newSocket.on('connect_error', handleConnectError);

    debug('Connecting socket for gameId:', gameId);
    newSocket.connect();
    setSocket(newSocket);

    return () => {
      clearTimeout(connectionTimeout);
      newSocket.off('connect', handleConnect);
      newSocket.off('disconnect', handleDisconnect);
      newSocket.off('joined', handleJoined);
      newSocket.off('gameState', handleGameState);
      newSocket.off('errorMessage', handleError);
      newSocket.off('connect_error', handleConnectError);
      newSocket.disconnect();
      setSocket(null);
      setPlayers([]);
      setCurrentPlayerId(null);
      setVotesRevealed(false);
      setEndVotingRequesterIds([]);
      setConnectionStatus('disconnected');
      setPendingJoin(null);
    };
  }, [gameId]);

  useEffect(() => {
    if (!socket || !pendingJoin || connectionStatus !== 'connected' || !gameId) {
      return;
    }

    joinNameRef.current = pendingJoin;
    socket.emit('joinGame', { gameId, name: pendingJoin });
    setPendingJoin(null);
  }, [socket, pendingJoin, connectionStatus, gameId]);

  const handleSetGameId = useCallback((id: string) => {
    setGameIdState((prev) => {
      if (prev && prev !== id && socket) socket.emit('leaveGame');
      if (prev !== id) {
        authoritativePlayerIdRef.current = null;
        setLocalSelectedVote(null);
      }
      return prev === id ? prev : id;
    });
  }, [socket]);

  const joinGame = useCallback(
    (name: string) => {
      if (!gameId) {
        return;
      }

      const trimmed = name.trim();
      if (!trimmed) {
        return;
      }
      joinNameRef.current = trimmed;

      if (socket && connectionStatus === 'connected') {
        socket.emit('joinGame', { gameId, name: trimmed });
        setPendingJoin(null);
      } else {
        setPendingJoin(trimmed);
      }
    },
    [connectionStatus, gameId, socket],
  );

  const castVote = useCallback(
    (vote: string) => {
      if (!gameId || !socket || !currentPlayerId || votesRevealed) {
        return;
      }

      const trimmedVote = vote.trim();
      if (!trimmedVote) {
        return;
      }

      setLocalSelectedVote(trimmedVote);
      socket.emit('castVote', { gameId, vote: trimmedVote });
    },
    [gameId, socket, currentPlayerId, votesRevealed],
  );

  const resetGame = useCallback(() => {
    debug('resetGame invoked', { gameId, hasSocket: Boolean(socket) });

    if (!gameId || !socket) {
      debug('resetGame aborted - missing identifiers');
      return;
    }

    socket.emit('resetGame', { gameId });
    debug('resetGame emitted to server', { gameId });
  }, [gameId, socket]);

  const toggleEndVoting = useCallback(() => {
    debug('toggleEndVoting invoked', { gameId, hasSocket: Boolean(socket) });

    if (!gameId || !socket || !currentPlayerId || votesRevealed) {
      debug('toggleEndVoting aborted');
      return;
    }

    socket.emit('toggleEndVoting', { gameId });
  }, [gameId, socket, currentPlayerId, votesRevealed]);

  const changeCardSet = useCallback(
    (newCardSet: CardSetType) => {
      debug('changeCardSet invoked', { gameId, newCardSet, hasSocket: Boolean(socket) });

      if (!gameId || !socket) {
        debug('changeCardSet aborted - missing identifiers');
        return;
      }

      socket.emit('changeCardSet', { gameId, cardSet: newCardSet });
      debug('changeCardSet emitted to server', { gameId, cardSet: newCardSet });
    },
    [gameId, socket],
  );

  // Apply stored card set preference when user is the only player in the game
  useEffect(() => {
    if (!socket || !gameId || !currentPlayerId) return;
    if (players.length !== 1) return;
    if (players[0]?.id !== currentPlayerId) return;

    const storedPreference = getStoredCardSet();
    if (!storedPreference || storedPreference === cardSet) return;

    debug('Applying stored card set preference', { storedPreference, currentCardSet: cardSet });
    socket.emit('changeCardSet', { gameId, cardSet: storedPreference });
  }, [socket, gameId, currentPlayerId, players.length]); // intentionally exclude cardSet to only run once

  // Merge local vote into players list so current player's table card shows their selection
  const playersWithLocalVote = useMemo(() => {
    if (votesRevealed || !localSelectedVote || !currentPlayerId) return players;
    return players.map((player) =>
      player.id === currentPlayerId ? { ...player, vote: localSelectedVote } : player
    );
  }, [players, votesRevealed, localSelectedVote, currentPlayerId]);

  const currentPlayer = useMemo(() => {
    if (!currentPlayerId) return null;
    const player = playersWithLocalVote.find((p) => p.id === currentPlayerId);
    if (!player) return null;
    return player;
  }, [currentPlayerId, playersWithLocalVote]);

  const endVotingRequestCount = endVotingRequesterIds.length;
  const hasRequestedEndVoting = Boolean(
    currentPlayerId && endVotingRequesterIds.includes(currentPlayerId),
  );

  const contextValue = useMemo(
    () => ({
      gameId,
      players: playersWithLocalVote,
      currentPlayer,
      currentPlayerId,
      cardOptions,
      cardSet,
      votesRevealed,
      endVotingRequestCount,
      hasRequestedEndVoting,
      connectionStatus,
      setGameId: handleSetGameId,
      joinGame,
      castVote,
      resetGame,
      changeCardSet,
      toggleEndVoting,
    }),
    [
      gameId,
      playersWithLocalVote,
      currentPlayer,
      currentPlayerId,
      cardOptions,
      cardSet,
      votesRevealed,
      endVotingRequestCount,
      hasRequestedEndVoting,
      connectionStatus,
      handleSetGameId,
      joinGame,
      castVote,
      resetGame,
      changeCardSet,
      toggleEndVoting,
    ],
  );

  return <GameContext.Provider value={contextValue}>{children}</GameContext.Provider>;
};

export const useGame = (): GameContextType => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};
